import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { ONDE_ATUA_SLOTS } from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';
import { SEO_TITLE_LENGTH } from 'volto-ministerio-publico-federal/widgets/CharCountWidget';
import { requireAltWhenOwnImage } from 'volto-ministerio-publico-federal/helpers/Image/altRequired';

export const messages = defineMessages({
  titleBlock: {
    id: 'mpf_onde_atua_block',
    defaultMessage: 'Onde o MPF atua? (capa)',
  },
  title: { id: 'mpf_onde_atua_title', defaultMessage: 'Título da faixa' },
  titleHelp: {
    id: 'mpf_onde_atua_title_help',
    defaultMessage: 'Em branco, usa "Onde o MPF atua?".',
  },
  tema: { id: 'mpf_onde_atua_tema', defaultMessage: 'Tema {slot}' },
  temaLabel: {
    id: 'mpf_onde_atua_tema_label',
    defaultMessage: 'Rótulo da aba',
  },
  temaLabelHelp: {
    id: 'mpf_onde_atua_tema_label_help',
    defaultMessage: 'Texto da pílula. Tema sem rótulo e sem cards não aparece.',
  },
  temaSubtitulo: {
    id: 'mpf_onde_atua_tema_subtitulo',
    defaultMessage: 'Chapéu',
  },
  temaSubtituloHelp: {
    id: 'mpf_onde_atua_tema_subtitulo_help',
    defaultMessage:
      'Linha acima do texto, ex.: "Conheça nossas frentes de atuação".',
  },
  temaTexto: { id: 'mpf_onde_atua_tema_texto', defaultMessage: 'Texto' },
  temaCtaLabel: {
    id: 'mpf_onde_atua_tema_cta_label',
    defaultMessage: 'Rótulo do botão',
  },
  temaCtaHref: {
    id: 'mpf_onde_atua_tema_cta_href',
    defaultMessage: 'Destino do botão',
  },
  temaCtaHrefHelp: {
    id: 'mpf_onde_atua_tema_cta_href_help',
    defaultMessage:
      'Escolha uma página do site ou cole um endereço. O botão só aparece com rótulo e destino preenchidos.',
  },
  temaCtaExternal: {
    id: 'mpf_onde_atua_tema_cta_external',
    defaultMessage: 'Abrir o botão em nova aba',
  },
  temaCards: { id: 'mpf_onde_atua_tema_cards', defaultMessage: 'Cards' },
  addTema: { id: 'mpf_onde_atua_add_tema', defaultMessage: 'Adicionar tema' },
  removeTema: {
    id: 'mpf_onde_atua_remove_tema',
    defaultMessage: 'Excluir tema {slot} do bloco',
  },
  removeTemaCancel: {
    id: 'mpf_onde_atua_remove_tema_cancel',
    defaultMessage: 'Cancelar',
  },
  removeTemaConfirm: {
    id: 'mpf_onde_atua_remove_tema_confirm',
    defaultMessage:
      'Excluir o tema {slot} do bloco? O rótulo, o texto, o botão e os cards dele serão apagados.',
  },
  card: { id: 'mpf_onde_atua_card', defaultMessage: 'Card' },
  cardTitle: { id: 'mpf_onde_atua_card_title', defaultMessage: 'Legenda' },
  cardTitleHelp: {
    id: 'mpf_onde_atua_card_title_help',
    defaultMessage:
      'Até 60 caracteres é o que o Google costuma exibir sem cortar. Acima disso a legenda ainda aparece inteira na capa.',
  },
  cardHref: { id: 'mpf_onde_atua_card_href', defaultMessage: 'Conteúdo' },
  cardHrefHelp: {
    id: 'mpf_onde_atua_card_href_help',
    defaultMessage:
      'Página do site ou endereço externo. Ao escolher um conteúdo, a legenda é preenchida com o título dele — legenda já editada não é sobrescrita.',
  },
  cardExternal: {
    id: 'mpf_onde_atua_card_external',
    defaultMessage: 'Abrir em nova aba',
  },
  fieldsetImage: {
    id: 'mpf_onde_atua_fieldset_image',
    defaultMessage: 'Imagem e vídeo',
  },
  cardImage: { id: 'mpf_onde_atua_card_image', defaultMessage: 'Imagem' },
  cardImageHelp: {
    id: 'mpf_onde_atua_card_image_help',
    defaultMessage:
      'Arraste um arquivo, escolha uma imagem do site ou cole uma URL. Em branco, usa a capa do vídeo ou a imagem do conteúdo escolhido.',
  },
  cardAlt: {
    id: 'mpf_onde_atua_card_alt',
    defaultMessage: 'Texto alternativo',
  },
  cardAltHelp: {
    id: 'mpf_onde_atua_card_alt_help',
    defaultMessage:
      'Descreva o conteúdo da imagem para leitores de tela. Obrigatório ao enviar imagem própria; herdando a imagem do conteúdo, herda-se também o texto alternativo dele.',
  },
  cardVideo: {
    id: 'mpf_onde_atua_card_video',
    defaultMessage: 'Vídeo do YouTube',
  },
  cardVideoHelp: {
    id: 'mpf_onde_atua_card_video_help',
    defaultMessage:
      'Cole o endereço do vídeo. Preenchido, o card ganha o selo de play e abre o player sobre a página.',
  },
});

/** Campos de um card do carrossel. */
const cardSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.card),
  addMessage: intl.formatMessage(messages.card),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'href', 'external'],
    },
    {
      id: 'image',
      title: intl.formatMessage(messages.fieldsetImage),
      fields: ['image', 'alt', 'videoUrl'],
    },
  ],
  properties: {
    // Contador em vez de corte: a legenda aparece inteira na capa, então o
    // tamanho é decisão editorial com aviso na hora de escrever. Ver
    // `widgets/CharCountWidget`.
    title: {
      title: intl.formatMessage(messages.cardTitle),
      description: intl.formatMessage(messages.cardTitleHelp),
      widget: 'charCount',
      recommendedLength: SEO_TITLE_LENGTH,
    },
    href: {
      title: intl.formatMessage(messages.cardHref),
      description: intl.formatMessage(messages.cardHrefHelp),
      widget: 'object_browser',
      mode: 'link',
      // `image_field`/`hasPreviewImage`/`image_scales` permitem herdar a
      // imagem do conteúdo sem o editor escolher arte dedicada.
      selectedItemAttrs: [
        '@id',
        '@type',
        'Title',
        'Description',
        'image_field',
        'hasPreviewImage',
        'image_scales',
        // Herda o texto alternativo do conteúdo quando a imagem é herdada.
        'descricaoImagem',
      ],
      allowExternals: true,
      initialPath: '/',
    },
    external: {
      title: intl.formatMessage(messages.cardExternal),
      type: 'boolean',
    },
    // `image` (e não `object_browser`): este widget navega no site, aceita
    // arrastar um arquivo para subir e também colar uma URL.
    image: {
      title: intl.formatMessage(messages.cardImage),
      description: intl.formatMessage(messages.cardImageHelp),
      widget: 'image',
    },
    alt: {
      title: intl.formatMessage(messages.cardAlt),
      description: intl.formatMessage(messages.cardAltHelp),
    },
    videoUrl: {
      title: intl.formatMessage(messages.cardVideo),
      description: intl.formatMessage(messages.cardVideoHelp),
      widget: 'url',
    },
  },
  required: ['title'],
});

/** Ids dos campos de um slot de tema. Fonte única do nome dos campos. */
export const temaFieldIds = (slot: number) => ({
  label: `tema${slot}Label`,
  subtitulo: `tema${slot}Subtitulo`,
  texto: `tema${slot}Texto`,
  ctaLabel: `tema${slot}CtaLabel`,
  ctaHref: `tema${slot}CtaHref`,
  ctaExternal: `tema${slot}CtaExternal`,
  cards: `tema${slot}Cards`,
});

/** Campo sem valor que desenha os botões do tema — ver `TemaAcoesWidget`. */
export const temaAcoesFieldId = (slot: number) => `tema${slot}Acoes`;

/** Ids dos três campos `object_list`, na ordem dos slots. */
export const ONDE_ATUA_CARD_FIELDS = ONDE_ATUA_SLOTS.map(
  (slot) => temaFieldIds(slot).cards,
);

/**
 * Schema do bloco "Onde o MPF atua?".
 *
 * Os três temas são slots FIXOS, gravados em campos achatados
 * (`tema1Label`, `tema1Cards`, ...) e não numa lista de temas: um
 * `object_list` dentro de outro não é suportado pelo Volto — o `ObjectWidget`
 * não repassa `activeObject`, e o lápis de um card nunca abriria o item certo.
 *
 * `activeObject`/`setActiveObject` são por slot: `activeObjects[slot]` traz o
 * índice aberto naquela lista e `-1` nas demais. `-1` (e não `undefined`) é o
 * que mantém o `ObjectListWidget` no modo controlado com tudo fechado — ver
 * `Destaques/schema.ts`.
 *
 * `visibleSlots` só corta os FIELDSETS: a sidebar mostra os temas até ali e o
 * resto entra pelo botão "Adicionar tema" (ver `Edit.tsx`). As `properties`
 * seguem com os três slots, para o `dataAdapter` e o form conhecerem todos os
 * campos. Com `onAddTema`, cada tema ganha no fim o campo do botão
 * "Adicionar tema", que só aparece no último visível. A lixeira de remover
 * vai no cabeçalho do fieldset — ver `Data.tsx`.
 */
export const mpfOndeAtuaSchema = ({
  intl,
  activeObjects = {},
  setActiveObject,
  visibleSlots = ONDE_ATUA_SLOTS.length,
  onAddTema,
}: {
  intl: IntlShape;
  activeObjects?: Record<number, number>;
  setActiveObject?: (slot: number, index: number) => void;
  visibleSlots?: number;
  onAddTema?: () => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    { id: 'default', title: 'Default', fields: ['title'] },
    ...ONDE_ATUA_SLOTS.filter((slot) => slot <= visibleSlots).map((slot) => ({
      id: `tema${slot}`,
      title: intl.formatMessage(messages.tema, { slot }),
      fields: [
        ...Object.values(temaFieldIds(slot)),
        ...(onAddTema ? [temaAcoesFieldId(slot)] : []),
      ],
    })),
  ],
  properties: {
    title: {
      title: intl.formatMessage(messages.title),
      description: intl.formatMessage(messages.titleHelp),
      placeholder: 'Onde o MPF atua?',
    },
    ...Object.fromEntries(
      ONDE_ATUA_SLOTS.flatMap((slot) => {
        const ids = temaFieldIds(slot);
        return [
          [
            ids.label,
            {
              title: intl.formatMessage(messages.temaLabel),
              description: intl.formatMessage(messages.temaLabelHelp),
            },
          ],
          [
            ids.subtitulo,
            {
              title: intl.formatMessage(messages.temaSubtitulo),
              description: intl.formatMessage(messages.temaSubtituloHelp),
            },
          ],
          [
            ids.texto,
            {
              title: intl.formatMessage(messages.temaTexto),
              widget: 'textarea',
            },
          ],
          [ids.ctaLabel, { title: intl.formatMessage(messages.temaCtaLabel) }],
          [
            ids.ctaHref,
            {
              title: intl.formatMessage(messages.temaCtaHref),
              description: intl.formatMessage(messages.temaCtaHrefHelp),
              widget: 'object_browser',
              mode: 'link',
              selectedItemAttrs: ['@id', '@type', 'Title'],
              allowExternals: true,
              initialPath: '/',
            },
          ],
          [
            ids.ctaExternal,
            {
              title: intl.formatMessage(messages.temaCtaExternal),
              type: 'boolean',
            },
          ],
          [
            ids.cards,
            {
              title: intl.formatMessage(messages.temaCards),
              widget: 'object_list',
              schema: cardSchema({ intl }),
              schemaExtender: requireAltWhenOwnImage,
              activeObject: activeObjects[slot] ?? -1,
              setActiveObject: setActiveObject
                ? (index: number) => setActiveObject(slot, index)
                : undefined,
            },
          ],
          [
            temaAcoesFieldId(slot),
            {
              widget: 'mpfOndeAtuaTemaAcoes',
              canAdd: slot === visibleSlots && slot < ONDE_ATUA_SLOTS.length,
              onAddTema,
            },
          ],
        ];
      }),
    ),
  },
  required: [],
});

export default mpfOndeAtuaSchema;
